import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';
import PatientDetailModal from '../components/PatientDetailModal';
import SearchBox, { loadMedicineOptions, loadPatientOptions } from '../components/SearchBox';
import PharmacyOperations from '../components/PharmacyOperations';

const PharmacyDashboard = () => {
  // Active section state
  const [activeSection, setActiveSection] = useState(null);

  // User permissions state
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // Patient search states
  const [searchTerm, setSearchTerm] = useState('');
  const [saleMedicineQuery, setSaleMedicineQuery] = useState('');
  const [purchaseMedicineQuery, setPurchaseMedicineQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Medicine reference data states
  const [medicines, setMedicines] = useState([]);
  const [medicinesLoading, setMedicinesLoading] = useState(true);
  const [medicinesError, setMedicinesError] = useState('');

  // Direct sale states
  const [saleMedicines, setSaleMedicines] = useState([]);
  const [saleMedicinesLoading, setSaleMedicinesLoading] = useState(false);
  const [saleMedicinesError, setSaleMedicinesError] = useState('');
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [saleQuantity, setSaleQuantity] = useState(1);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [saleNotes, setSaleNotes] = useState('');
  const [saleLoading, setSaleLoading] = useState(false);
  const [saleSuccess, setSaleSuccess] = useState('');

  // Medicine sales history states
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesHistoryLoading, setSalesHistoryLoading] = useState(true);
  const [salesHistoryError, setSalesHistoryError] = useState('');

  // Medicine purchase states
  const [purchaseMedicines, setPurchaseMedicines] = useState([]);
  const [purchaseMedicinesLoading, setPurchaseMedicinesLoading] = useState(false);
  const [purchaseMedicinesError, setPurchaseMedicinesError] = useState('');
  const [selectedPurchaseMedicine, setSelectedPurchaseMedicine] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const [unitPurchasePrice, setUnitPurchasePrice] = useState(0);
  const [supplierName, setSupplierName] = useState('');
  const [supplierContact, setSupplierContact] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [purchasePaymentMethod, setPurchasePaymentMethod] = useState('Cash');
  const [purchaseNotes, setPurchaseNotes] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState('');

  // Medicine purchase history states
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(true);
  const [purchaseHistoryError, setPurchaseHistoryError] = useState('');

  // Profit margin analysis states
  const [profitAnalysis, setProfitAnalysis] = useState(null);
  const [profitAnalysisLoading, setProfitAnalysisLoading] = useState(false);
  const [profitAnalysisError, setProfitAnalysisError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Helper function to check if user has permission for a gadget
  const hasPermission = (gadgetName, permissionType = 'VIEW') => {
    const permission = userPermissions[gadgetName];
    return permission && permission[permissionType] === true;
  };

  // Function to fetch user permissions
  const fetchUserPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPermissionsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/gadgetPermissions/my-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const permissions = await response.json();
        setUserPermissions(permissions);
        console.log('Pharmacy Dashboard - User permissions:', permissions);
      } else {
        console.error('Failed to fetch user permissions');
        setUserPermissions({});
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setUserPermissions({});
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Fetch medicines from reference data
  const fetchMedicines = async () => {
    setMedicinesLoading(true);
    setMedicinesError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setMedicinesError('Authentication token not found. Please log in again.');
      setMedicinesLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/medicines`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        setMedicines(Array.isArray(data) ? data : []);
      } else {
        setMedicinesError(data.message || 'Failed to fetch medicines.');
      }
    } catch (err) {
      setMedicinesError(`Network error: ${err.message}`);
    } finally {
      setMedicinesLoading(false);
    }
  };

  // Patient search function
  const handleSearch = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setSearchError('Authentication token not found. Please log in again.');
      setSearchLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/patients/search?search=${encodeURIComponent(searchTerm)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        setSearchResults(Array.isArray(data) ? data : []);
      } else {
        setSearchError(data.message || 'Failed to search patients.');
      }
    } catch (err) {
      setSearchError(`Network error: ${err.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleViewPatientDetails = (patient) => {
    setSelectedPatient(patient);
  };

  const handleClosePatientDetails = () => {
    setSelectedPatient(null);
  };

  // Medicine search for sale
  const handleMedicineSearch = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSaleMedicines([]);
      return;
    }

    setSaleMedicinesLoading(true);
    setSaleMedicinesError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setSaleMedicinesError('Authentication token not found. Please log in again.');
      setSaleMedicinesLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/medicines?searchTerm=${encodeURIComponent(searchTerm)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        const medicines = Array.isArray(data) ? data : [];
        console.log('Medicine search results:', medicines);
        setSaleMedicines(medicines);
      } else {
        setSaleMedicinesError(data.message || 'Failed to search medicines.');
      }
    } catch (err) {
      setSaleMedicinesError(`Network error: ${err.message}`);
    } finally {
      setSaleMedicinesLoading(false);
    }
  };

  const handleSelectMedicine = (medicine) => {
    console.log('Medicine selected:', medicine);
    setSelectedMedicine(medicine);
    setSaleQuantity(1);
    setSaleMedicines([]);
  };

  const handleProcessSale = async () => {
    if (!selectedMedicine) {
      alert('Please select a medicine first.');
      return;
    }

    if (!selectedMedicine.MEDICINE_ID) {
      alert('Invalid medicine selected. Please search and select a medicine again.');
      return;
    }

    setSaleLoading(true);
    setSaleSuccess('');
    const token = localStorage.getItem('token');

    if (!token) {
      alert('Authentication token not found. Please log in again.');
      setSaleLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/medicineSales`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            MEDICINE_ID: selectedMedicine.MEDICINE_ID,
            QUANTITY_SOLD: saleQuantity,
            CUSTOMER_NAME: customerName,
            CUSTOMER_PHONE: customerPhone,
            PAYMENT_METHOD: paymentMethod,
            NOTES: saleNotes
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSaleSuccess(`Sale completed successfully! Sale ID: ${data.sale.SALE_ID}`);
        // Reset form
        setSelectedMedicine(null);
        setSaleQuantity(1);
        setCustomerName('');
        setCustomerPhone('');
        setPaymentMethod('Cash');
        setSaleNotes('');
        // Refresh medicines and sales history
        fetchMedicines();
        fetchSalesHistory();
      } else {
        alert(data.message || 'Failed to process sale.');
      }
    } catch (err) {
      alert(`Network error: ${err.message}`);
    } finally {
      setSaleLoading(false);
    }
  };

  // Fetch sales history
  const fetchSalesHistory = async () => {
    setSalesHistoryLoading(true);
    setSalesHistoryError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setSalesHistoryError('Authentication token not found. Please log in again.');
      setSalesHistoryLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/medicineSales?limit=20`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        setSalesHistory(Array.isArray(data.sales) ? data.sales : []);
      } else {
        setSalesHistoryError(data.message || 'Failed to fetch sales history.');
      }
    } catch (err) {
      setSalesHistoryError(`Network error: ${err.message}`);
    } finally {
      setSalesHistoryLoading(false);
    }
  };

  // Medicine search for purchase
  const handlePurchaseMedicineSearch = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setPurchaseMedicines([]);
      return;
    }

    setPurchaseMedicinesLoading(true);
    setPurchaseMedicinesError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setPurchaseMedicinesError('Authentication token not found. Please log in again.');
      setPurchaseMedicinesLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/medicines?searchTerm=${encodeURIComponent(searchTerm)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        const medicines = Array.isArray(data) ? data : [];
        console.log('Purchase medicine search results:', medicines);
        setPurchaseMedicines(medicines);
      } else {
        setPurchaseMedicinesError(data.message || 'Failed to search medicines.');
      }
    } catch (err) {
      setPurchaseMedicinesError(`Network error: ${err.message}`);
    } finally {
      setPurchaseMedicinesLoading(false);
    }
  };

  const handleSelectPurchaseMedicine = (medicine) => {
    console.log('Purchase medicine selected:', medicine);
    setSelectedPurchaseMedicine(medicine);
    setPurchaseQuantity(1);
    setUnitPurchasePrice(medicine.PURCHASE_PRICE || 0);
    setPurchaseMedicines([]);
  };

  const handleProcessPurchase = async () => {
    if (!selectedPurchaseMedicine) {
      alert('Please select a medicine first.');
      return;
    }

    if (!selectedPurchaseMedicine.MEDICINE_ID) {
      alert('Invalid medicine selected. Please search and select a medicine again.');
      return;
    }

    setPurchaseLoading(true);
    setPurchaseSuccess('');
    const token = localStorage.getItem('token');

    if (!token) {
      alert('Authentication token not found. Please log in again.');
      setPurchaseLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/medicinePurchases`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            MEDICINE_ID: selectedPurchaseMedicine.MEDICINE_ID,
            QUANTITY_PURCHASED: purchaseQuantity,
            UNIT_PURCHASE_PRICE: unitPurchasePrice,
            SUPPLIER_NAME: supplierName,
            SUPPLIER_CONTACT: supplierContact,
            BATCH_NUMBER: batchNumber,
            EXPIRATION_DATE: expirationDate,
            PAYMENT_METHOD: purchasePaymentMethod,
            NOTES: purchaseNotes
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setPurchaseSuccess(`Purchase completed successfully! Purchase ID: ${data.purchase.PURCHASE_ID}`);
        // Reset form
        setSelectedPurchaseMedicine(null);
        setPurchaseQuantity(1);
        setUnitPurchasePrice(0);
        setSupplierName('');
        setSupplierContact('');
        setBatchNumber('');
        setExpirationDate('');
        setPurchasePaymentMethod('Cash');
        setPurchaseNotes('');
        // Refresh medicines and purchase history
        fetchMedicines();
        fetchPurchaseHistory();
      } else {
        alert(data.message || 'Failed to process purchase.');
      }
    } catch (err) {
      alert(`Network error: ${err.message}`);
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Fetch purchase history
  const fetchPurchaseHistory = async () => {
    setPurchaseHistoryLoading(true);
    setPurchaseHistoryError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setPurchaseHistoryError('Authentication token not found. Please log in again.');
      setPurchaseHistoryLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/medicinePurchases?limit=20`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        setPurchaseHistory(Array.isArray(data.purchases) ? data.purchases : []);
      } else {
        setPurchaseHistoryError(data.message || 'Failed to fetch purchase history.');
      }
    } catch (err) {
      setPurchaseHistoryError(`Network error: ${err.message}`);
    } finally {
      setPurchaseHistoryLoading(false);
    }
  };

  // Fetch profit margin analysis
  const fetchProfitAnalysis = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setProfitAnalysisLoading(true);
    setProfitAnalysisError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setProfitAnalysisError('Authentication token not found. Please log in again.');
      setProfitAnalysisLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/medicinePurchases/analytics/profit-margin?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();

      if (response.ok) {
        setProfitAnalysis(data);
      } else {
        setProfitAnalysisError(data.message || 'Failed to fetch profit analysis.');
      }
    } catch (err) {
      setProfitAnalysisError(`Network error: ${err.message}`);
    } finally {
      setProfitAnalysisLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch(searchTerm);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Fetch medicines, sales history, and purchase history on component mount
  useEffect(() => {
    fetchUserPermissions();
    fetchMedicines();
    fetchSalesHistory();
    fetchPurchaseHistory();
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '2rem auto', padding: '2rem' }}>
      <h2>Pharmacy Dashboard</h2>
      
      {/* Dashboard Gadgets/Buttons */}
      {permissionsLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
          Loading dashboard permissions...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        
        {/* Patient Search Gadget */}
        {hasPermission('Patient Management') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>🔍 Patient Search</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Search and view patient information
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'patientSearch' ? null : 'patientSearch')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'patientSearch' ? '#dc3545' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'patientSearch' ? 'Close Search' : 'Search Patients'}
            </button>
          </div>
        )}

        {/* Direct Sale Gadget */}
        {hasPermission('Medicine Sales') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>💰 Direct Sale</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Process medicine sales to customers
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'directSale' ? null : 'directSale')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'directSale' ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'directSale' ? 'Close Sale' : 'Start Sale'}
            </button>
          </div>
        )}

        {/* Medicine Purchase Gadget */}
        {hasPermission('Medicine Purchases') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📦 Medicine Purchase</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Add new stock and record purchases
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'medicinePurchase' ? null : 'medicinePurchase')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'medicinePurchase' ? '#dc3545' : '#ffc107',
                color: activeSection === 'medicinePurchase' ? 'white' : '#212529',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'medicinePurchase' ? 'Close Purchase' : 'Add Stock'}
            </button>
          </div>
        )}

        {/* Profit Analysis Gadget */}
        {hasPermission('Profit Analysis') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📊 Profit Analysis</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              Calculate profit margins and generate reports
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'profitAnalysis' ? null : 'profitAnalysis')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'profitAnalysis' ? '#dc3545' : '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'profitAnalysis' ? 'Close Analysis' : 'View Reports'}
            </button>
          </div>
        )}

        {/* Medicine Reference Gadget */}
        {hasPermission('Medicine Reference') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📚 Medicine Reference</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              View all medicines in inventory
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'medicineReference' ? null : 'medicineReference')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'medicineReference' ? '#dc3545' : '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'medicineReference' ? 'Close Reference' : 'View Medicines'}
            </button>
          </div>
        )}

        {/* Sales History Gadget */}
        {hasPermission('Medicine Sales') && (
          <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>📈 Sales History</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
              View recent sales transactions
            </p>
            <button
              onClick={() => setActiveSection(activeSection === 'salesHistory' ? null : 'salesHistory')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: activeSection === 'salesHistory' ? '#dc3545' : '#20c997',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {activeSection === 'salesHistory' ? 'Close History' : 'View Sales'}
            </button>
          </div>
        )}

        {[
          ['Low Stock and Expiry', 'lowStock', 'Low stock and expiry', 'Medicines to reorder and batches nearing expiry', 'View stock', 'Close stock', '#b45309'],
          ['Purchase History', 'purchaseHistory', 'Purchase history', 'Receipts, suppliers, batches, and prices', 'View receipts', 'Close receipts', '#0f766e'],
          ['Returns and Adjustments', 'adjustments', 'Returns and adjustments', 'Returns, damage, expiry, and shelf counts', 'Update stock', 'Close adjustments', '#9a3412'],
          ['Suppliers', 'suppliers', 'Suppliers', 'Who supplied each medicine and the last price', 'View suppliers', 'Close suppliers', '#1d4ed8'],
          ['Daily Counter Summary', 'counter', 'Daily counter', 'Today’s sales, refunds, and profit', 'View counter', 'Close counter', '#6f42c1']
        ].map(([permission, section, title, text, closed, open, color]) => hasPermission(permission) && (
          <div key={section} style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>{title}</h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>{text}</p>
            <button
              onClick={() => setActiveSection(activeSection === section ? null : section)}
              style={{ width: '100%', padding: '10px', backgroundColor: activeSection === section ? '#dc3545' : color, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              {activeSection === section ? open : closed}
            </button>
          </div>
        ))}
        
        {/* No gadgets available message */}
        {!permissionsLoading && !['Patient Management', 'Medicine Sales', 'Medicine Purchases', 'Profit Analysis', 'Medicine Reference', 'Low Stock and Expiry', 'Purchase History', 'Returns and Adjustments', 'Suppliers', 'Daily Counter Summary'].some((name) => hasPermission(name)) && (
          <div style={{ 
            gridColumn: '1 / -1', 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#6c757d',
            backgroundColor: '#f8f9fa',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}>
            <h3>No Dashboard Access</h3>
            <p>You don't have permission to access any dashboard gadgets. Please contact your administrator.</p>
          </div>
        )}
      </div>
      )}

      {/* Conditional Sections */}
      {activeSection === 'patientSearch' && hasPermission('Patient Management') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Patient Search</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
            <SearchBox
              placeholder="Search patients by name, ID, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              loadOptions={loadPatientOptions}
              onSelect={(option) => setSearchTerm(option.item.PATIENT_NAME || '')}
            />
          </div>

          {searchLoading && <p>Searching patients...</p>}
          {searchError && <p style={{ color: 'red' }}>Error: {searchError}</p>}

          {searchResults.length > 0 && (
            <div>
              <h4>Search Results:</h4>
              {searchResults.map((patient) => (
                <div
                  key={patient.PATIENT_ID}
                  style={{
                    border: '1px solid #eee',
                    padding: '10px',
                    marginBottom: '5px',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <strong>{patient.PATIENT_NAME}</strong> - {patient.PATIENT_ID} - {patient.CONTACT_NUMBER}
                  </div>
                  <button
                    onClick={() => handleViewPatientDetails(patient)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          )}

          {searchTerm && !searchLoading && searchResults.length === 0 && !searchError && (
            <p>No patients found matching your search.</p>
          )}
        </div>
      )}

      {activeSection === 'directSale' && hasPermission('Medicine Sales') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Direct Sale</h3>
          
          {/* Medicine Search for Sale */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Search Medicine:
            </label>
            <SearchBox
              placeholder="Search medicines by name..."
              value={saleMedicineQuery}
              onChange={(e) => {
                setSaleMedicineQuery(e.target.value);
                handleMedicineSearch(e.target.value);
              }}
              loadOptions={loadMedicineOptions}
              onSelect={(option) => {
                setSaleMedicineQuery(option.item.MEDICINE_NAME || '');
                handleSelectMedicine(option.item);
              }}
            />
            
            {saleMedicinesLoading && <p>Searching medicines...</p>}
            {saleMedicinesError && <p style={{ color: 'red' }}>Error: {saleMedicinesError}</p>}
            
            {saleMedicines.length > 0 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', marginTop: '5px' }}>
                {saleMedicines.map((medicine) => (
                  <div
                    key={medicine.MEDICINE_ID}
                    onClick={() => handleSelectMedicine(medicine)}
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: '#f9f9f9'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e9e9e9'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f9f9f9'}
                  >
                    <strong>{medicine.MEDICINE_NAME}</strong> - {medicine.STRENGTH} - Stock: {medicine.QUANTITY_IN_STOCK} - Price: ₹{medicine.SELLING_PRICE}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Medicine and Sale Form */}
          {selectedMedicine && (
            <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', marginBottom: '1rem' }}>
              <h4>Selected Medicine:</h4>
              <p><strong>Name:</strong> {selectedMedicine.MEDICINE_NAME}</p>
              <p><strong>Strength:</strong> {selectedMedicine.STRENGTH}</p>
              <p><strong>Available Stock:</strong> {selectedMedicine.QUANTITY_IN_STOCK}</p>
              <p><strong>Price per unit:</strong> ₹{selectedMedicine.SELLING_PRICE}</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Quantity to Sell:
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={selectedMedicine.QUANTITY_IN_STOCK}
                    value={saleQuantity}
                    onChange={(e) => setSaleQuantity(parseInt(e.target.value) || 1)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Total Amount:
                  </label>
                  <input
                    type="text"
                    value={`₹${(selectedMedicine.SELLING_PRICE * saleQuantity).toFixed(2)}`}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      boxSizing: 'border-box',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Customer Name:
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Payment Method:
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Notes:
                </label>
                <textarea
                  value={saleNotes}
                  onChange={(e) => setSaleNotes(e.target.value)}
                  placeholder="Optional notes..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleProcessSale}
                  disabled={saleLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: saleLoading ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: saleLoading ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {saleLoading ? 'Processing...' : 'Process Sale'}
                </button>
                
                <button
                  onClick={() => {
                    setSelectedMedicine(null);
                    setSaleQuantity(1);
                    setCustomerName('');
                    setCustomerPhone('');
                    setPaymentMethod('Cash');
                    setSaleNotes('');
                    setSaleSuccess('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>

              {saleSuccess && (
                <div style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                  {saleSuccess}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeSection === 'medicinePurchase' && hasPermission('Medicine Purchases') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Medicine Purchase</h3>
          
          {/* Medicine Search for Purchase */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Search Medicine:
            </label>
            <SearchBox
              placeholder="Search medicines by name..."
              value={purchaseMedicineQuery}
              onChange={(e) => {
                setPurchaseMedicineQuery(e.target.value);
                handlePurchaseMedicineSearch(e.target.value);
              }}
              loadOptions={loadMedicineOptions}
              onSelect={(option) => {
                setPurchaseMedicineQuery(option.item.MEDICINE_NAME || '');
                handleSelectPurchaseMedicine(option.item);
              }}
            />
            
            {purchaseMedicinesLoading && <p>Searching medicines...</p>}
            {purchaseMedicinesError && <p style={{ color: 'red' }}>Error: {purchaseMedicinesError}</p>}
            
            {purchaseMedicines.length > 0 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px', marginTop: '5px' }}>
                {purchaseMedicines.map((medicine) => (
                  <div
                    key={medicine.MEDICINE_ID}
                    onClick={() => handleSelectPurchaseMedicine(medicine)}
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: '#f9f9f9'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e9e9e9'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f9f9f9'}
                  >
                    <strong>{medicine.MEDICINE_NAME}</strong> - {medicine.STRENGTH} - Current Stock: {medicine.QUANTITY_IN_STOCK}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Medicine and Purchase Form */}
          {selectedPurchaseMedicine && (
            <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', marginBottom: '1rem' }}>
              <h4>Selected Medicine:</h4>
              <p><strong>Name:</strong> {selectedPurchaseMedicine.MEDICINE_NAME}</p>
              <p><strong>Strength:</strong> {selectedPurchaseMedicine.STRENGTH}</p>
              <p><strong>Current Stock:</strong> {selectedPurchaseMedicine.QUANTITY_IN_STOCK}</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Quantity to Purchase:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={purchaseQuantity}
                    onChange={(e) => setPurchaseQuantity(parseInt(e.target.value) || 1)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Unit Purchase Price (₹):
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={unitPurchasePrice}
                    onChange={(e) => setUnitPurchasePrice(parseFloat(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Supplier Name:
                  </label>
                  <input
                    type="text"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Batch Number:
                  </label>
                  <input
                    type="text"
                    value={batchNumber}
                    onChange={(e) => setBatchNumber(e.target.value)}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleProcessPurchase}
                  disabled={purchaseLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: purchaseLoading ? '#6c757d' : '#ffc107',
                    color: purchaseLoading ? 'white' : '#212529',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: purchaseLoading ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {purchaseLoading ? 'Processing...' : 'Process Purchase'}
                </button>
                
                <button
                  onClick={() => {
                    setSelectedPurchaseMedicine(null);
                    setPurchaseQuantity(1);
                    setUnitPurchasePrice(0);
                    setSupplierName('');
                    setSupplierContact('');
                    setBatchNumber('');
                    setExpirationDate('');
                    setPurchasePaymentMethod('Cash');
                    setPurchaseNotes('');
                    setPurchaseSuccess('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>

              {purchaseSuccess && (
                <div style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                  {purchaseSuccess}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeSection === 'profitAnalysis' && hasPermission('Profit Analysis') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Profit Margin Analysis</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Start Date:
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                End Date:
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <button
                onClick={fetchProfitAnalysis}
                disabled={profitAnalysisLoading}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: profitAnalysisLoading ? '#6c757d' : '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: profitAnalysisLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {profitAnalysisLoading ? 'Analyzing...' : 'Generate Report'}
              </button>
            </div>
          </div>

          {profitAnalysisError && <p style={{ color: 'red' }}>Error: {profitAnalysisError}</p>}

          {profitAnalysis && (
            <div>
              <h4>Profit Analysis Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ padding: '1rem', backgroundColor: '#e3f2fd', borderRadius: '4px', textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 0.5rem 0', color: '#1976d2' }}>Total Sales</h5>
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2' }}>
                    ₹{profitAnalysis.summary.totalSales.toFixed(2)}
                  </p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#fff3e0', borderRadius: '4px', textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 0.5rem 0', color: '#f57c00' }}>Total Purchases</h5>
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#f57c00' }}>
                    ₹{profitAnalysis.summary.totalPurchases.toFixed(2)}
                  </p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#e8f5e8', borderRadius: '4px', textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 0.5rem 0', color: '#388e3c' }}>Total Profit</h5>
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#388e3c' }}>
                    ₹{profitAnalysis.summary.totalProfit.toFixed(2)}
                  </p>
                </div>
                <div style={{ padding: '1rem', backgroundColor: '#f3e5f5', borderRadius: '4px', textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 0.5rem 0', color: '#7b1fa2' }}>Profit Margin</h5>
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#7b1fa2' }}>
                    {profitAnalysis.summary.overallProfitMargin.toFixed(2)}%
                  </p>
                </div>
              </div>

              <h4>Detailed Analysis by Medicine</h4>
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Medicine</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Sales</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Purchases</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Profit</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitAnalysis.details.map((item, index) => (
                      <tr key={index}>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>{item.medicineName || item.medicineId}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>₹{item.totalSales.toFixed(2)}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>₹{item.totalPurchases.toFixed(2)}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: item.profit >= 0 ? '#388e3c' : '#d32f2f' }}>
                          ₹{item.profit.toFixed(2)}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: item.profitMargin >= 0 ? '#388e3c' : '#d32f2f' }}>
                          {item.profitMargin.toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSection === 'medicineReference' && hasPermission('Medicine Reference') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Medicine Reference Data</h3>
          
          {medicinesLoading && <p>Loading medicines...</p>}
          {medicinesError && <p style={{ color: 'red' }}>Error: {medicinesError}</p>}

          {!medicinesLoading && !medicinesError && medicines.length === 0 && (
            <p>No medicines found in reference data.</p>
          )}

          {!medicinesLoading && !medicinesError && medicines.length > 0 && (
            <div>
              <p><strong>Total Medicines:</strong> {medicines.length}</p>
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Medicine Name</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Strength</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Supplier</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Price</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicines.map((medicine) => (
                      <tr key={medicine.MEDICINE_ID}>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>{medicine.MEDICINE_NAME}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>{medicine.STRENGTH}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>{medicine.MEDICINE_TYPE}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>{medicine.SUPPLIER_NAME}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>₹{medicine.SELLING_PRICE}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>{medicine.QUANTITY_IN_STOCK}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSection === 'salesHistory' && hasPermission('Medicine Sales') && (
        <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          <h3>Recent Sales History</h3>
          
          {salesHistoryLoading && <p>Loading sales history...</p>}
          {salesHistoryError && <p style={{ color: 'red' }}>Error: {salesHistoryError}</p>}

          {!salesHistoryLoading && !salesHistoryError && salesHistory.length === 0 && (
            <p>No sales recorded yet.</p>
          )}

          {!salesHistoryLoading && !salesHistoryError && salesHistory.length > 0 && (
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Sale ID</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Medicine</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Qty</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Amount</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Customer</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {salesHistory.map((sale) => (
                    <tr key={sale.SALE_ID}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>{sale.SALE_ID}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>{sale.MEDICINE_NAME}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>{sale.QUANTITY_SOLD}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>₹{sale.TOTAL_AMOUNT}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>{sale.CUSTOMER_NAME || 'Walk-in'}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>{new Date(sale.SALE_DATE).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {['lowStock', 'purchaseHistory', 'adjustments', 'suppliers', 'counter'].includes(activeSection) && (
        <PharmacyOperations section={activeSection} />
      )}

      {/* Patient Detail Modal */}
      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          onClose={handleClosePatientDetails}
        />
      )}
    </div>
  );
};

export default PharmacyDashboard;
